import DocPageLayout, { Section } from "@/components/DocPageLayout";

export default function VendorHandbook() {
  return (
    <DocPageLayout
      subtitle="For Sellers"
      title="Vendor Handbook"
      intro="Everything a Hunt57 vendor needs to know — tiers, subscription, listings, POs, combined shipping, strikes, promotions. Read this before your first list."
    >
      <Section title="Becoming a Vendor">
        <p>Two paths from your Account page:</p>
        <ul className="list-disc pl-5 space-y-2 mt-2">
          <li><strong>Application</strong>: fill out the vendor application (why, experience, socials). Admin reviews in 1-3 business days. Requires a Stripe payment method on file.</li>
          <li><strong>Referral code</strong>: paste a HUNTV-XXXXXX code from a Tier 3 vendor. Instant vendor status, same Stripe requirement.</li>
        </ul>
        <p>Both paths require accepting the Vendor Terms — you confirm all sales are private, off-platform, and Hunt57 is not a party.</p>
      </Section>

      <Section title="Subscription Pricing">
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><strong>Days 1-30</strong>: Free trial. Full vendor privileges. Cancel any time — no charge.</li>
          <li><strong>Day 31 (launch promo, first year only)</strong>: If not canceled, <span className="line-through text-zinc-500">$69.99</span> <strong className="text-[#FFD700]">$49.99</strong> is charged. Same date becomes your annual anniversary going forward.</li>
          <li><strong>Year 2 onward</strong>: <strong>$69.99</strong> renewal on your anniversary. Cancel any time from Account → Billing Portal.</li>
          <li><strong>Founding Members</strong>: Exempt from all fees for life while account is in good standing.</li>
        </ul>
        <p>Billing goes through Stripe. A payment method must be on file <strong>before</strong> the trial begins — for both the application path and the referral code path.</p>
      </Section>

      <Section title="Vendor Tiers">
        <ul className="list-disc pl-5 space-y-2 mt-2">
          <li><strong>Tier 1 (Starter)</strong>: 25 active listings max · $50 maximum price · default for new vendors.</li>
          <li><strong>Tier 2 (Established)</strong>: No listing count limit · no price cap. Auto-promoted after 5 confirmed sales with Excellent standing.</li>
          <li><strong>Tier 3 (Trusted)</strong>: Same as Tier 2 + can generate 3 referral vouchers per year (HUNTV-XXXXXX codes). Auto-promoted after 25 confirmed sales.</li>
        </ul>
        <p>Standing drops (strikes, PO defaults) can demote you back to Tier 1 temporarily.</p>
      </Section>

      <Section title="Creating a Listing">
        <p>Go to /sell. The form auto-generates a title from Year + Brand + Set + Player, but you can override. Required: front photo, price, sport, base/insert/variation choice. Strongly recommended: back photo, notes for any imperfections.</p>
        <p><strong className="text-white">Accepts Offers:</strong> default on. Toggle off for FIRM listings — the red FIRM chip appears on the tile and offer-messages become strike-worthy.</p>
        <p><strong className="text-white">Bulk upload:</strong> Sell → Bulk Upload accepts spreadsheets for large drops. See the template link in that flow.</p>
      </Section>

      <Section title="Combined Shipping">
        <p>Turn on Combined Shipping in Account settings to encourage multi-card purchases. You define:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><strong>Initial charge</strong>: what you charge for the first card (e.g. $5.99).</li>
          <li><strong>Per additional card</strong>: incremental charge for each extra card (e.g. $0.25).</li>
          <li><strong>Max per package</strong>: after this count, a new full charge kicks in (e.g. 20).</li>
        </ul>
        <p>Once enabled, every listing of yours displays the policy inline. Buyers see the blue COMBO chip on browse tiles.</p>
      </Section>

      <Section title="Purchase Order Flow (Seller Side)">
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Buyer opens PO → notification bell fires → your listing flips to <strong>Pending</strong>.</li>
          <li>You have <strong>72 hours to Validate</strong>. Not validating = auto-expiry + a hit to your standing.</li>
          <li>Buyer pays you off-platform. Verify receipt of funds before shipping.</li>
          <li>Ship promptly (typical ship time set in your profile — respect it).</li>
          <li>Both parties mark Complete. Feedback opens. Sale count increments.</li>
        </ul>
      </Section>

      <Section title="Managing Inventory">
        <p><span className="font-mono text-[#FF5F1F]">/my-listings</span> has tabs for Active / Pending / Hold / Sold / Removed. Use Hold to temporarily pause a card (out of state, price research, pending trade). Sold cards auto-move when a PO completes.</p>
        <p>Bulk edit: select multiple listings and Edit price, shipping, or offers-toggle across all.</p>
      </Section>

      <Section title="Payment Paths">
        <p>Set up in Account → Payment Paths. You need <strong>at least one</strong>: PayPal link, Cash App handle, or Venmo link. These are shown to buyers on the listing detail page and inside the PO modal — buyers pay you directly.</p>
        <p>Recommended: enable PayPal Goods & Services for buyer protection. It builds trust even though it fees you 3%.</p>
      </Section>

      <Section title="Referral Vouchers (Tier 3 only)">
        <p>Once you hit Tier 3, you can generate up to 3 HUNTV-XXXXXX referral codes per calendar year. Give them to trusted friends in the hobby to fast-track them to vendor status. Both of you get a bonus tier point on their first confirmed sale.</p>
      </Section>

      <Section title="Best Practices">
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Photograph cards on a plain neutral background under natural light. Front + back always.</li>
          <li>Disclose everything: creases, print lines, edge wear, autograph fading, grading company inconsistencies.</li>
          <li>Ship in penny sleeve + toploader + team bag inside a bubble mailer. High-value cards: PWE upgrades or tracked shipping.</li>
          <li>Respond to messages within 24 hours. Ghosting kills sales.</li>
          <li>Honor your combined-shipping policy — buyers keep receipts.</li>
        </ul>
      </Section>
    </DocPageLayout>
  );
}
